import { TFunction } from '@lyearn/i18n';
import { Typography } from '@lyearn/ui';
import { FUTURE_DATE_FILTER_ITEMS } from '@/components/DateFilter/constants';
import { tagsFilterConfig } from '@/components/FilterManager/configs/tags/tagsFilterConfig';
import {
  FilesEntityConfig,
  PROJECTS_AND_LISTS_QUERY_PARAMS,
} from '@/components/FilterManager/EntityFilter/entity/files';
import { UserEntityConfig } from '@/components/FilterManager/EntityFilter/entity/user';
import { FilterManager, FilterState, SortAndFilterItem } from '@/components/FilterManager/types';
import { ColumnShape } from '@/components/Table/types';
import * as ActionItem from '@/modules/tasks/features/actionItems/components/ActionItem';
import { ActionItemFragment } from '@/modules/tasks/features/actionItems/graphql/fragments/generated/ActionItem';
import {
  getTaskPrioritiesGroups,
  TASK_SORT_ITEMS,
} from '@/modules/tasks/views/TasksDashboard/utils/sortAndFilterItems';
import { ActionItemsFilters, TagAvailableOnEnum } from '@/types/schema';

import SelectAll from '../components/SelectAll';
import SelectTaskCheckbox from '../components/SelectTaskCheckbox';
import {
  assigneeRowStyles,
  dueDateRowStyles,
  headerStyles,
  optionsStyles,
  taskStatusRowStyles,
} from '../styles';

export const getSortAndFilterItems = (t: TFunction): SortAndFilterItem[] => {
  return [
    {
      type: FilterManager.Sort,
      isEnabled: true,
      items: TASK_SORT_ITEMS,
    },
    {
      type: FilterManager.Filter,
      id: 'priorities',
      defaultPlaceholder: t('Common:Filter.Priority'),
      isEnabled: true,
      groups: getTaskPrioritiesGroups(t),
    },
    {
      type: FilterManager.DateRange,
      id: 'dueDate',
      defaultPlaceholder: t('Common:Filter.DueDate'),
      options: FUTURE_DATE_FILTER_ITEMS,
      isEnabled: true,
    },
    {
      type: FilterManager.Entity,
      isEnabled: true,
      id: 'assignedToIds',
      defaultPlaceholder: t('Common:Filter.Assignee'),
      listConfig: UserEntityConfig,
    },
    {
      type: FilterManager.Entity,
      listConfig: FilesEntityConfig,
      defaultPlaceholder: t('Common:Filter.List'),
      isEnabled: true,
      id: 'fileIds',
      entity: 'files',
      queryParams: PROJECTS_AND_LISTS_QUERY_PARAMS,
    },
    {
      ...tagsFilterConfig(t),
      queryParams: {
        availableOn: [TagAvailableOnEnum.Task],
        showProviders: true,
      },
    },
  ];
};

const renderHeader = (title: string) => (
  <Typography color="text-text-placeholder" variant="label-02">
    {title}
  </Typography>
);

export const getTableColumns = (t: TFunction): ColumnShape<ActionItemFragment>[] => [
  {
    key: 'statusAndName',
    headerRenderer: () => renderHeader(t('Common:Task')),
    cellRenderer: () => (
      <>
        <ActionItem.Status size={18} />
        <ActionItem.Name className="ms-8" numberOfLines={1} variant="body-short-02" />
      </>
    ),
    width: 400,
    flexShrink: 0,
    flexGrow: 1,
    className: taskStatusRowStyles,
    headerClassName: headerStyles,
  },
  {
    key: 'priority',
    headerRenderer: () => renderHeader(t('Common:Filter.Priority')),
    align: 'center',
    width: 128,
    cellRenderer: () => <ActionItem.Priority />,
    flexShrink: 0,
  },
  {
    key: 'dueDate',
    headerRenderer: () => renderHeader(t('Task:Details.DueDate')),
    width: 128,
    cellRenderer: () => <ActionItem.DatePicker />,
    flexShrink: 0,
    className: dueDateRowStyles,
    headerClassName: headerStyles,
  },
  {
    key: 'assignee',
    headerRenderer: () => renderHeader(t('Common:Filter.Assignee')),
    width: 300,
    cellRenderer: () => (
      <ActionItem.AssignMenu
        profileSettings={{
          showName: true,
        }}
      />
    ),
    flexShrink: 0,
    className: assigneeRowStyles,
    headerClassName: headerStyles,
  },
  {
    key: 'options',
    width: 42,
    align: 'right',
    headerRenderer: () => <SelectAll />,
    cellRenderer: ({ rowData }) => <SelectTaskCheckbox id={rowData.id} />,
    flexShrink: 0,
    className: optionsStyles,
    headerClassName: optionsStyles,
  },
];

export const adaptAppliedFilters = (filter: FilterState, search: string): ActionItemsFilters => {
  return {
    ...filter,
    title: search,
  };
};
